import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { TimezoneReading } from '@/api/settings'
import { TIMEZONE_SETTING_KEY } from '@/domain/timezone'
import { apiFetch } from '~/api/client'

// Taken from the domain rather than spelled again: the card writes the key the
// night reads, and two spellings would leave a household's zone saved and every
// date it is stamped with still cut at UTC.
export { DEFAULT_TIMEZONE, TIMEZONE_SETTING_KEY, isTimezone } from '@/domain/timezone'
export type { TimezoneReading }

const TIMEZONE_KEY = ['settings', 'timezone'] as const

export function useTimezone(): UseQueryResult<TimezoneReading> {
  return useQuery({
    queryKey: TIMEZONE_KEY,
    queryFn: () => apiFetch<TimezoneReading>('/settings/timezone'),
  })
}

/**
 * Only what has not been stamped yet moves, so nothing else is refetched.
 *
 * A date already on file was a record of the day it was taken, and the zone it
 * was read in is part of what it recorded. Invalidating the figures here would
 * redraw a history that has not changed, which is the redraw a household would
 * read as its past moving.
 */
export function useSaveTimezone(): UseMutationResult<unknown, Error, string> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (value: string) =>
      apiFetch(`/settings/keys/${TIMEZONE_SETTING_KEY}`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: TIMEZONE_KEY })
    },
  })
}

/**
 * A moment as a household in `zone` would read it off a clock.
 *
 * The locale is named rather than inherited, for the reason the dates are: a
 * host numbering system other than Latin would render an hour the household
 * cannot compare to anything else on the page. Twenty-four hour, because the
 * whole point of the sentence is that an hour may be surprising and "2:32" that
 * turns out to be the afternoon would bury exactly what it is there to show.
 */
export function momentIn(at: string, zone: string): string | undefined {
  const parsed = new Date(at)
  if (Number.isNaN(parsed.getTime())) return undefined
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      dateStyle: 'medium',
      timeStyle: 'short',
      hourCycle: 'h23',
    }).format(parsed)
  } catch {
    return undefined
  }
}

/**
 * The zone this browser is in, or nothing where it will not say.
 *
 * Offered rather than saved. A browser's zone is where the reader is sitting
 * now, which is usually the household's and is not the same claim — somebody
 * reading on holiday would otherwise move the day their whole history is cut
 * at, silently, by opening a page.
 */
export function browserTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return zone === '' ? undefined : zone
  } catch {
    return undefined
  }
}

/**
 * The zones a household can pick from, as the runtime lists them, with the
 * browser's own first where it is not already at the top.
 *
 * Asked of the runtime rather than kept here for the same reason the server
 * validates that way: zones are added, renamed and merged, and a list in the
 * product would start refusing what the product itself accepts. Where the
 * runtime will not enumerate them — older Safari — the field is still typable
 * and the server still validates, so the household is short a menu rather than
 * short the setting.
 */
export function timezoneChoices(): string[] {
  const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf
  let zones: string[] = []
  try {
    zones = supported?.('timeZone') ?? []
  } catch {
    zones = []
  }
  const browser = browserTimezone()
  if (browser === undefined || zones.length === 0) return zones
  return [browser, ...zones.filter((zone) => zone !== browser)]
}
